import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  SimpleGrid,
  Spinner,
  Switch,
  Text,
  UnorderedList,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { LockIcon } from "@chakra-ui/icons";
import PageHeader from "../components/PageHeader";
import PoolCardScroller from "../components/PoolCardScroller";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getMyWcPools, getAllWcPools, createWcPool, joinWcPool , getWcPoolWindow } from "../api/worldCup";
import worldCupLogo from "../assets/logos/worldcup.png";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";

function formatDraftDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}


// WC 2026 season id (set after seeding)
const WC_SEASON_ID = 2;

export default function WorldCupHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [myPools,      setMyPools]      = useState([]);
  const [allPools,     setAllPools]     = useState([]);
  const [loadingPools, setLoadingPools] = useState(true);
  const [poolError,    setPoolError]    = useState("");
  const [joining,      setJoining]      = useState(null);

  const [newPoolName,     setNewPoolName]     = useState("");
  const [newPoolDepth,    setNewPoolDepth]    = useState("");
  const [newTeamsPerUser, setNewTeamsPerUser] = useState("");
  const [newMaxUsers,     setNewMaxUsers]     = useState("");
  const [isPrivate,       setIsPrivate]       = useState(false);
  const [creating,        setCreating]        = useState(false);
  const [createError,     setCreateError]     = useState("");

  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();

  function handleStartNewPool() {
    setCreateError("");
    if (!newPoolName.trim()) { setPoolError("Pool name is required."); return; }
    setPoolError("");
    onCreateOpen();
  }

  function handleCreateClose() {
    setNewPoolName("");
    setNewPoolDepth("");
    setIsPrivate(false);
    setNewTeamsPerUser("");
    setNewMaxUsers("");
    setCreateError("");
    onCreateClose();
  }

  useEffect(() => {
    loadPools();
  }, []);

  async function loadPools() {
    setLoadingPools(true);
    setPoolError("");
    try {
      const [mine, all] = await Promise.all([
        getMyWcPools(WC_SEASON_ID),
        getAllWcPools(WC_SEASON_ID),
      ]);
      setMyPools(mine);
      setAllPools(all);
    } catch (err) {
      setPoolError(err?.response?.data?.detail || "Failed to load pools.");
      setMyPools([]);
      setAllPools([]);
    } finally {
      setLoadingPools(false);
    }
  }

  const joinablePools = useMemo(() => {
    const myPoolIds = new Set(myPools.map((p) => p.id));
    return allPools.filter((p) => !myPoolIds.has(p.id));
  }, [myPools, allPools]);

  // Lock pool creation/joining once the tournament has actually begun. The
  // pool data still carries the signal, but the API's window is authoritative
  // and also covers the too-early state before pools exist at all.
  const [poolWindow, setPoolWindow] = useState(null);
  useEffect(() => {
    getWcPoolWindow(WC_SEASON_ID).then(setPoolWindow).catch(() => setPoolWindow(null));
  }, []);

  const anyMatchPlayed = useMemo(
    () => [...myPools, ...allPools].some((p) => p.any_match_played),
    [myPools, allPools]
  );
  const seasonStarted = poolWindow ? !!poolWindow.season_started : anyMatchPlayed;
  const creationOpen = poolWindow ? !!poolWindow.pool_creation_open : !anyMatchPlayed;

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  function handleOpenPool(poolId) {
    navigate(`/world-cup/current/${poolId}`);
  }

  async function handleJoin(poolId) {
    setJoining(poolId);
    try {
      await joinWcPool(WC_SEASON_ID, poolId);
      await loadPools();
    } catch (err) {
      setPoolError(err?.response?.data?.detail || "Failed to join pool.");
    } finally {
      setJoining(null);
    }
  }

  async function handleCreate() {
    setCreateError("");
    const tpu = parseInt(newTeamsPerUser, 10);
    if (!tpu || tpu < 1 || tpu > 10) { setCreateError("Number of countries per user must be between 1 and 10."); return; }
    const maxU = parseInt(newMaxUsers, 10);
    if (!maxU || maxU < 2 || maxU > 12) { setCreateError("Number of users must be between 2 and 12."); return; }
    setCreating(true);
    try {
      const pool = await createWcPool(WC_SEASON_ID, {
        name: newPoolName.trim(),
        teams_per_user: tpu,
        max_users: maxU,
        pool_depth: newPoolDepth ? parseInt(newPoolDepth, 10) : null,
        is_private: isPrivate,
      });
      onCreateClose();
      navigate(`/world-cup/setup/${pool.id}`);
    } catch (err) {
      setCreateError(err?.response?.data?.detail || "Failed to create pool.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white" overflowX="hidden">
      <PageHeader
        title="WORLD CUP POOL LOBBY"
        onBack={() => navigate("/")}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={10}>
        <VStack align="stretch" spacing={8}>
          <VStack align="start" spacing={2}>
            <Text fontSize="lg" color="whiteAlpha.900">
              Welcome to the World Cup Pool lobby. Enter your current game, view open pools, or start a new pool.
            </Text>
            {creationOpen && (
              <Alert status="success" borderRadius="md" mt={1} color="green.900">
                <AlertIcon flexShrink={0} />
                <Text ml={2}>
                  The <strong>{poolWindow?.season_name || "next"}</strong> season is available
                  for set up — create your pool below!
                </Text>
              </Alert>
            )}
          </VStack>

          {poolError && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {poolError}
            </Alert>
          )}

          {/* My Pools + Join row */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>

            {/* Your Active Games */}
            <Box
              bg="white"
              color="black"
              border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg"
              boxShadow="lg"
              p={8}
              minH="260px"
              minW="0"
              position="relative"
              overflow="hidden"
            
              opacity={!loadingPools && myPools.length === 0 ? 0.5 : 1}
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
                opacity={0.06}
                pointerEvents="none"
                zIndex={0}
              >
                <img src={worldCupLogo} alt="" style={{ width: "420px" }} />
              </Box>

              <VStack align="stretch" spacing={4} h="100%" position="relative" zIndex={1}>
                <Heading size="md" color={TOP_BAR_BLUE}>Your Active Games</Heading>
                <Text color="gray.700">Pools you belong to for the 2026 World Cup.</Text>

                {loadingPools ? (
                  <Flex flex="1" align="center" justify="center"><Spinner /></Flex>
                ) : myPools.length === 0 ? (
                  <Flex flex="1" align="center" justify="center">
                    <Text fontSize="lg" color="gray.500" textAlign="center">
                      You are not currently in any pools.
                    </Text>
                  </Flex>
                ) : (
                  <Box maxH={{ base: "240px", sm: "unset" }} overflowY={{ base: "auto", sm: "visible" }} mt={1}>
                    {/* Mobile: compact list */}
                    <VStack align="stretch" spacing={0} display={{ base: "flex", sm: "none" }}>
                      {myPools.map((pool) => (
                        <Flex key={pool.id} align="center" py={2} borderBottom="1px solid" borderColor="gray.100">
                          <Text fontWeight="semibold" fontSize="sm" flex="1" mr={3} noOfLines={1}>{pool.name}</Text>
                          <HStack spacing={3} flexShrink={0}>
                            {pool.creator_user_id === user?.id && (
                              <Button variant="link" size="sm" color={TOP_BAR_BLUE} onClick={() => navigate(`/world-cup/setup/${pool.id}`)}>Edit</Button>
                            )}
                            <Button variant="link" size="sm" color={TOP_BAR_BLUE} fontWeight="bold" onClick={() => handleOpenPool(pool.id)}>Open</Button>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                    {/* Desktop: card grid */}
                    <PoolCardScroller display={{ base: "none", sm: "block" }}>
                      {myPools.map((pool) => (
                        <Box key={pool.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="gray.50">
                          <VStack align="center" spacing={2}>
                            <Text fontWeight="bold" textAlign="center">{pool.name}</Text>
                            <Text fontSize="sm" color="gray.600" textAlign="center">Participants: {pool.user_count}</Text>
                            <Text fontSize="sm" color="gray.600" textAlign="center">Total Pot: {pool.total_pot != null ? `$${pool.total_pot}` : "None"}</Text>
                            {pool.status === "pending" && (
                              <Text fontSize="sm" color="gray.600" textAlign="center">Draft: {formatDraftDate(pool.draft_starts_at) || "TBD"}</Text>
                            )}
                            <Text fontSize="xs" color="gray.400" textAlign="center" textTransform="capitalize">Status: {pool.status}</Text>
                            <HStack spacing={2} justify="center">
                              {pool.creator_user_id === user?.id && (
                                <Button size="sm" variant="outline" borderColor={TOP_BAR_BLUE} color={TOP_BAR_BLUE} onClick={() => navigate(`/world-cup/setup/${pool.id}`)} _hover={{ bg: "gray.100" }}>Edit Pool</Button>
                              )}
                              <Button size="sm" bg={TOP_BAR_BLUE} color="white" onClick={() => handleOpenPool(pool.id)} _hover={{ bg: "#00004a" }}>Open Pool</Button>
                            </HStack>
                          </VStack>
                        </Box>
                      ))}
                    </PoolCardScroller>
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Join an Open Pool */}
            <Box
              bg="white"
              color="black"
              border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg"
              boxShadow="lg"
              p={8}
              minH="260px"
              minW="0"
              opacity={creationOpen ? 1 : 0.45}
              pointerEvents={seasonStarted ? "none" : "auto"}
              position="relative"
              overflow="hidden"
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
                opacity={0.06}
                pointerEvents="none"
                zIndex={0}
              >
                <img src={worldCupLogo} alt="" style={{ width: "420px" }} />
              </Box>

              <VStack align="stretch" spacing={5} h="100%" position="relative" zIndex={1}>
                <Heading size="md" color={TOP_BAR_BLUE}>Join an Open Pool</Heading>

                {seasonStarted ? (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    Joining a pool is not available once the season has started
                  </Alert>
                ) : loadingPools ? (
                  <Flex flex="1" align="center" justify="center"><Spinner /></Flex>
                ) : joinablePools.length === 0 ? (
                  <Flex flex="1" align="center" justify="center">
                    <Text fontSize="lg" color="gray.500" textAlign="center">
                      No open pools available right now.
                    </Text>
                  </Flex>
                ) : (
                  <Box maxH={{ base: "240px", sm: "unset" }} overflowY={{ base: "auto", sm: "visible" }} mt={1}>
                    {/* Mobile: compact list */}
                    <VStack align="stretch" spacing={0} display={{ base: "flex", sm: "none" }}>
                      {joinablePools.map((pool) => {
                        const isStarted = pool.status !== "pending";
                        const privateNoInvite = pool.is_private && !pool.user_was_invited;
                        const isDisabled = privateNoInvite || isStarted;
                        return (
                          <Flex key={pool.id} align="center" py={2} borderBottom="1px solid" borderColor="gray.100" opacity={isDisabled ? 0.5 : 1}>
                            <HStack flex="1" mr={3} spacing={1} overflow="hidden">
                              {pool.is_private && <LockIcon boxSize={3} color="gray.400" flexShrink={0} />}
                              <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>{pool.name}</Text>
                            </HStack>
                            <Button variant="link" size="sm" color={TOP_BAR_BLUE} fontWeight="bold" isLoading={joining === pool.id} isDisabled={isDisabled} onClick={() => !isDisabled && handleJoin(pool.id)}>Join</Button>
                          </Flex>
                        );
                      })}
                    </VStack>
                    {/* Desktop: card grid */}
                    <PoolCardScroller display={{ base: "none", sm: "block" }}>
                      {joinablePools.map((pool) => {
                        const isStarted = pool.status !== "pending";
                        const privateNoInvite = pool.is_private && !pool.user_was_invited;
                        const isDisabled = privateNoInvite || isStarted;
                        return (
                          <Box key={pool.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="gray.50" opacity={isStarted ? 0.6 : 1}>
                            <VStack align="center" spacing={2}>
                              <HStack spacing={2} justify="center">
                                {pool.is_private && <LockIcon boxSize={3} color="gray.500" />}
                                <Text fontWeight="bold" textAlign="center">{pool.name}</Text>
                              </HStack>
                              <Text fontSize="sm" color="gray.600" textAlign="center">Participants: {pool.user_count}</Text>
                              {pool.total_pot != null && (
                                <Text fontSize="sm" color="gray.600" textAlign="center">Total Pot: ${pool.total_pot}</Text>
                              )}
                              {formatDraftDate(pool.draft_starts_at) && (
                                <Text fontSize="sm" color="gray.600" textAlign="center">Draft: {formatDraftDate(pool.draft_starts_at)}</Text>
                              )}
                              {pool.is_private && !isStarted && (
                                <Text fontSize="xs" color={pool.user_was_invited ? "green.600" : "gray.400"} textAlign="center">
                                  {pool.user_was_invited ? "You're invited" : "Invite only"}
                                </Text>
                              )}
                              <Button size="sm" bg={TOP_BAR_BLUE} color="white" isLoading={joining === pool.id} isDisabled={isDisabled} onClick={() => !isDisabled && handleJoin(pool.id)} _hover={{ bg: isDisabled ? undefined : "#00004a" }}>Join Pool</Button>
                            </VStack>
                          </Box>
                        );
                      })}
                    </PoolCardScroller>
                  </Box>
                )}
              </VStack>
            </Box>
          </SimpleGrid>

          {/* Rules + New Pool */}
          <Box
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={8}
          >
            <VStack align="stretch" spacing={8}>

              {/* Start New Pool heading */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>Want to Start a New Pool?</Heading>
                {seasonStarted && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    New game can't be created mid tournament
                  </Alert>
                )}
              </Box>

              <Divider borderColor="gray.300" />

              {/* Rules */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>Rules</Heading>
                <UnorderedList spacing={3} pl={5}>
                  <ListItem>
                    Draft <strong>countries</strong> via a randomized snake draft. The number of teams per user is set by the pool creator.
                  </ListItem>
                  <ListItem>
                    The draft order is randomized by the pool admin and snakes each round (1→last, last→1, etc.).
                  </ListItem>
                  <ListItem>
                    You earn points based on your countries' results throughout the tournament.
                  </ListItem>
                  <ListItem>
                    <strong>One swap</strong> is allowed per entry after the group stage and before the knockout stage begins.
                    You may only swap in an undrafted team that advanced out of the group stage.
                    Points accumulated by the removed team are forfeited.
                  </ListItem>
                  <ListItem>
                    The swap order is determined by total points (lowest points gets to swap first).
                  </ListItem>
                </UnorderedList>
              </Box>

              <Divider borderColor="gray.300" />

              {/* Scoring */}
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>Scoring</Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  <Box>
                    <Text fontWeight="bold" mb={2} color={TOP_BAR_BLUE}>Group Stage (per game)</Text>
                    <UnorderedList spacing={1} pl={4}>
                      <ListItem>Win: <strong>+3 pts</strong></ListItem>
                      <ListItem>Draw: <strong>+1 pt</strong></ListItem>
                      <ListItem>Goal scored: <strong>+1 pt</strong></ListItem>
                      <ListItem>Goal allowed: <strong>−0.5 pts</strong></ListItem>
                    </UnorderedList>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" mb={2} color={TOP_BAR_BLUE}>Knockout Stage (per game)</Text>
                    <UnorderedList spacing={1} pl={4}>
                      <ListItem>Win (90 min / ET): <strong>+3 pts</strong></ListItem>
                      <ListItem>Win via penalties: <strong>+2 pts</strong></ListItem>
                    </UnorderedList>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" mb={2} color={TOP_BAR_BLUE}>Advancement Bonuses (cumulative)</Text>
                    <UnorderedList spacing={1} pl={4}>
                      <ListItem>Make Round of 32: <strong>+2 pts</strong></ListItem>
                      <ListItem>Make Round of 16: <strong>+3 pts</strong></ListItem>
                      <ListItem>Make Quarterfinal: <strong>+5 pts</strong></ListItem>
                      <ListItem>Make Semifinal: <strong>+7 pts</strong></ListItem>
                      <ListItem>Make Final: <strong>+9 pts</strong></ListItem>
                      <ListItem>Champion: <strong>+6 pts</strong> (additional)</ListItem>
                    </UnorderedList>
                  </Box>
                </SimpleGrid>
              </Box>

              <Divider borderColor="gray.300" />

              {/* Start New Pool form */}
              <Box opacity={creationOpen ? 1 : 0.5}>
                  <VStack align="stretch" spacing={4}>
                    {poolError && !createError && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />{poolError}
                      </Alert>
                    )}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl isRequired isDisabled={!creationOpen}>
                        <FormLabel>Pool Name</FormLabel>
                        <Input
                          value={newPoolName}
                          onChange={(e) => setNewPoolName(e.target.value)}
                          placeholder="Enter pool name"
                        />
                      </FormControl>
                      <FormControl isDisabled={!creationOpen}>
                        <FormLabel>Pool Depth ($)</FormLabel>
                        <Input
                          type="number"
                          min={1}
                          value={newPoolDepth}
                          onChange={(e) => setNewPoolDepth(e.target.value)}
                          placeholder="Optional buy-in amount"
                        />
                      </FormControl>
                    </SimpleGrid>
                    <HStack>
                      <Button
                        bg={TOP_BAR_BLUE}
                        color="white"
                        isDisabled={!creationOpen || !newPoolName.trim()}
                        _hover={{ bg: "#00004a" }}
                        onClick={handleStartNewPool}
                      >
                        Start New Pool
                      </Button>
                    </HStack>
                    {!creationOpen && !seasonStarted && (
                      <Text color="gray.700">
                        Once next season setup is available, this page will allow users to create a
                        new pool before the season begins.
                      </Text>
                    )}
                  </VStack>
              </Box>

            </VStack>
          </Box>
        </VStack>
      </Container>

      {/* Create Pool Modal */}
      <Modal isOpen={isCreateOpen} onClose={handleCreateClose} isCentered size="md" motionPreset="scale">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg">
          <ModalHeader color={TOP_BAR_BLUE}>Pool Settings</ModalHeader>
          <ModalCloseButton color={TOP_BAR_BLUE} />
          <ModalBody>
            <VStack spacing={5} align="stretch">
              {createError && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />{createError}
                </Alert>
              )}

              {/* Summary from lobby */}
              <SimpleGrid columns={2} spacing={4}>
                <Box bg="gray.50" borderRadius="md" p={3}>
                  <Text fontSize="xs" color="gray.500" mb={1}>Pool Name</Text>
                  <Text fontWeight="semibold">{newPoolName}</Text>
                </Box>
                <Box bg="gray.50" borderRadius="md" p={3}>
                  <Text fontSize="xs" color="gray.500" mb={1}>Buy-in</Text>
                  <Text fontWeight="semibold">{newPoolDepth ? `$${newPoolDepth}` : "None"}</Text>
                </Box>
              </SimpleGrid>

              <Divider />

              {/* Private toggle */}
              <FormControl>
                <HStack spacing={3} align="center">
                  <Switch
                    id="private-pool"
                    isChecked={isPrivate}
                    onChange={(e) => setIsPrivate(e.target.checked)}
                    colorScheme="blue"
                  />
                  <FormLabel htmlFor="private-pool" mb={0} cursor="pointer">
                    <HStack spacing={2}>
                      <LockIcon boxSize={3} />
                      <Text fontWeight="medium">Private Pool (invite only)</Text>
                    </HStack>
                  </FormLabel>
                </HStack>
                <Text fontSize="sm" color="gray.500" mt={1} pl={10}>
                  {isPrivate
                    ? "Only invited users can join this pool."
                    : "Anyone can find and join this pool from the lobby."}
                </Text>
              </FormControl>

              <Divider />

              {/* User count */}
              <FormControl isRequired>
                <FormLabel>{isPrivate ? "Number of Users" : "Max Users (2–12)"}</FormLabel>
                <NumberInput
                  min={2}
                  max={12}
                  value={newMaxUsers}
                  onChange={(v) => setNewMaxUsers(v)}
                >
                  <NumberInputField placeholder={isPrivate ? "e.g. 8" : "e.g. 10"} />
                </NumberInput>
                {!isPrivate && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Pool closes to new members once this limit is reached.
                  </Text>
                )}
              </FormControl>

              {/* Countries per user */}
              <FormControl isRequired>
                <FormLabel>Countries Per User (1–10)</FormLabel>
                <NumberInput
                  min={1}
                  max={10}
                  value={newTeamsPerUser}
                  onChange={(v) => setNewTeamsPerUser(v)}
                >
                  <NumberInputField placeholder="e.g. 4" />
                </NumberInput>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="outline" onClick={handleCreateClose}>Cancel</Button>
              <Button
                bg={TOP_BAR_BLUE}
                color="white"
                _hover={{ bg: "#00004a" }}
                isLoading={creating}
                isDisabled={!newTeamsPerUser || !newMaxUsers}
                onClick={handleCreate}
              >
                Create Pool
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
